import { CanonicalDataType } from "@/shared/domain/topics";
import {
  FILTER_CONDITIONS,
  type FilterCondition,
  type FilterStage,
  type StageCombinator,
} from "@/shared/state/visualization/schema/v1/log-panel";

import {
  ALL_COLUMNS_ID,
  ALL_COLUMNS_NAME,
  LOG_TIME_ID,
  LOG_TIME_NAME,
  type ColumnDefinition,
} from "./columnDefinition";

export {
  FILTER_CONDITIONS,
  type FilterCondition,
  type FilterStage,
  type StageCombinator,
};

// Lives in the state layer with the rest of the persisted-key contract; kept
// exported here so panel code has one import site for filter state helpers.
export { migratePersistedColumnFilterKeys } from "@/shared/state/visualization/schema/v1/log-panel";

const validConditions = new Set<string>(FILTER_CONDITIONS);

export interface ColumnFilter {
  stages: FilterStage[];
  combinator: StageCombinator;
}

/** Filters keyed by {@link ColumnDefinition.columnId}. */
export type ColumnFilterMap = Record<string, ColumnFilter>;

/** Filterable columns keyed by {@link ColumnDefinition.columnId}. */
export type ColumnMap = Record<string, ColumnDefinition>;

/**
 * What the filter UI renders from: the filters currently in effect, paired
 * with the columns they can apply to.
 *
 * Derived, not stored: `deriveLiveColumnFilters` rebuilds it from
 * `panel.config.columnFilters` and the panel's data whenever either changes,
 * and edits commit back to that config. It is a memoized computation, never a
 * second home for filter state.
 */
export interface LiveColumnFilters {
  /** See `LogSearchEngine` for how these are applied. */
  filters: ColumnFilterMap;
  /** The columns a filter may be set on, including the synthetic entries. */
  columns: ColumnMap;
}

/**
 * Multi-stage filter shape (v2, current).
 */
interface MultiStageFilter {
  stages: FilterStage[];
  combinator?: StageCombinator;
}

function isMultiStageFilter(saved: unknown): saved is MultiStageFilter {
  if (
    typeof saved !== "object" ||
    saved === null ||
    !("stages" in saved) ||
    !Array.isArray((saved as MultiStageFilter).stages)
  ) {
    return false;
  }
  const stages = (saved as MultiStageFilter).stages;
  // A stageless entry filters nothing, yet it is still a key in the map, so
  // restoring it would read as an active filter wherever those keys are
  // counted: a pill with no conditions, and "All columns" hidden from the
  // picker. `applyFilterAction` deletes a column's entry when its last stage
  // goes, so the running app never writes this shape; `columnFilterValueSchema`
  // accepts it all the same, and an external writer can produce it.
  if (stages.length === 0) {
    return false;
  }
  for (const stage of stages) {
    if (
      typeof stage !== "object" ||
      stage === null ||
      typeof stage.value !== "string" ||
      typeof stage.condition !== "string" ||
      !validConditions.has(stage.condition)
    ) {
      return false;
    }
  }
  return true;
}

// ---------------------------------------------------------------------------
// Selectors — pure derivations from a ColumnFilterMap
// ---------------------------------------------------------------------------

/**
 * Synthetic column IDs that are not subject to the mutual exclusivity
 * constraint between all-columns and per-column filters.
 */
const SYNTHETIC_COLUMN_IDS = new Set([ALL_COLUMNS_ID, LOG_TIME_ID]);

/**
 * Returns true if `filters` holds at least one filter for a real per-column
 * entry (i.e., any filter key that isn't a synthetic column). LOG_TIME_ID is
 * excluded because users should be able to filter by time range simultaneously
 * with an all-columns search.
 */
export function hasPerColumnFilters(filters: ColumnFilterMap): boolean {
  return Object.keys(filters).some((key) => !SYNTHETIC_COLUMN_IDS.has(key));
}

/**
 * Returns true if `filters` holds an active filter for the synthetic
 * "all columns" entry.
 */
export function hasAllColumnsFilter(filters: ColumnFilterMap): boolean {
  return Object.hasOwn(filters, ALL_COLUMNS_ID);
}

/**
 * Returns true if adding a filter for `columnId` would violate the mutual
 * exclusivity constraint between all-columns and per-column filters.
 *
 * Synthetic columns (LOG_TIME_ID) are exempt: users can filter by time
 * range alongside an all-columns search.
 */
export function isExclusivityViolation(
  columnId: string,
  filters: ColumnFilterMap,
): boolean {
  // Synthetic columns (other than ALL_COLUMNS itself) never conflict.
  if (SYNTHETIC_COLUMN_IDS.has(columnId) && columnId !== ALL_COLUMNS_ID) {
    return false;
  }
  return (
    (columnId === ALL_COLUMNS_ID && hasPerColumnFilters(filters)) ||
    (!SYNTHETIC_COLUMN_IDS.has(columnId) && hasAllColumnsFilter(filters))
  );
}

/**
 * Drops the all-columns entry when a per-column filter is restored beside it.
 * A log-time filter is exempt: a time window is meant to combine with either.
 *
 * Only `applyFilterAction` and the picker enforce that exclusivity, and neither
 * sees a config that already holds both. `columnFilterValueSchema` does not
 * reject the mix either, deliberately: a v1 workspace config that fails
 * validation throws out of `VizConfig.from_obj` and replaces the whole
 * `/visualize` route with an error page, so rejecting one filter would cost
 * every panel.
 *
 * Left alone, the pair makes `isExclusivityViolation` true for the all-columns
 * entry and for every per-column one. `applyFilterAction` then refuses every
 * later edit to either side, including edits to the filters already on screen,
 * and the picker narrows to "Log time" alone. Deleting a pill or clearing is
 * the only way out.
 *
 * The all-columns entry is the one dropped: there is a single one of it against
 * however many per-column filters. Deriving never writes back, so the dropped
 * entry stays in config until the next edit commits the live map over it.
 */
function withoutConflictingAllColumnsFilter(
  filters: ColumnFilterMap,
  columns: Record<string, ColumnDefinition>,
): ColumnFilterMap {
  // Only a per-column filter the panel can run conflicts; one no column backs
  // is withheld by `deriveLiveColumnFilters` and must not cost the all-columns
  // entry its place.
  const hasBackedPerColumnFilter = Object.keys(filters).some(
    (key) => !SYNTHETIC_COLUMN_IDS.has(key) && Object.hasOwn(columns, key),
  );
  if (!hasBackedPerColumnFilter || !hasAllColumnsFilter(filters)) {
    return filters;
  }
  const remaining = { ...filters };
  delete remaining[ALL_COLUMNS_ID];
  return remaining;
}

/**
 * Builds {@link LiveColumnFilters} from the panel's columns and saved filters.
 *
 * Shapes that `columnFilterValueSchema` tolerates but the panel cannot run are
 * normalized here rather than surfaced as errors:
 *
 * - v1 plain string: `"tool"` becomes
 *   `{ stages: [{ value: "tool", condition: "contains" }], combinator: "or" }`
 * - v2 multi-stage object: stages rebuilt field by field, `combinator`
 *   defaulted to `"or"`, a non-string `value2` dropped
 * - unrecognized shape, or empty `stages`: left out entirely
 * - all-columns entry saved beside a per-column one: dropped (see
 *   `withoutConflictingAllColumnsFilter`)
 */
export function buildLiveColumnFilters(
  dynamicColumns: ColumnDefinition[],
  savedFilters: Record<string, string | MultiStageFilter> | undefined,
): LiveColumnFilters {
  const columns: ColumnMap = {
    [ALL_COLUMNS_ID]: { columnId: ALL_COLUMNS_ID, name: ALL_COLUMNS_NAME },
    [LOG_TIME_ID]: {
      columnId: LOG_TIME_ID,
      name: LOG_TIME_NAME,
      dataType: CanonicalDataType.Timestamp,
    },
  };
  for (const column of dynamicColumns) {
    columns[column.columnId] = column;
  }
  // Restore every saved filter, whether or not its column is present.
  // `deriveLiveColumnFilters` is the one place that decides which of them the
  // panel can run: it withholds the ones no column backs while leaving them in
  // config, so opening a layout against a dataset missing that column does not
  // by itself drop them.
  const filters: ColumnFilterMap = {};
  for (const [columnId, saved] of Object.entries(savedFilters ?? {})) {
    if (typeof saved === "string") {
      filters[columnId] = {
        stages: [{ value: saved, condition: "contains" }],
        combinator: "or",
      };
    } else if (isMultiStageFilter(saved)) {
      // Stages are rebuilt field by field rather than spread: the layouts path
      // never validates panel config, so `value2` can arrive as null or a
      // number even though the schema types it as an optional string.
      filters[columnId] = {
        stages: saved.stages.map((s) => {
          const stage: FilterStage = {
            value: s.value,
            condition: s.condition,
          };
          if ("value2" in s && typeof s.value2 === "string") {
            stage.value2 = s.value2;
          }
          return stage;
        }),
        combinator: saved.combinator ?? "or",
      };
    }
  }
  return {
    filters: withoutConflictingAllColumnsFilter(filters, columns),
    columns,
  };
}
